
import axios from 'axios';
import { namespace } from 'vuex-class';
import { Route } from 'vue-router';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { sendLog } from '@/api/client-logs';
import ApiCancelError from '@/resources/error/ApiCancelError';
import ApiModalError from '@/resources/error/ApiModalError';
import { CameraChildErrorCode } from '@/resources/error/CameraChildErrorCode';
import { ErrorCode } from '@/resources/error/ErrorCode';
import { ErrorType } from '@/resources/error/ErrorType';
import PhotoType from '@/resources/enum/PhotoType';
import IdDocType from '@/resources/enum/IdDocType';
import ColorButton from '@/components/ColorButton.vue';
import ModalDefault from '@/components/ModalDefault.vue';
import ModalDefaultPopupBodyCameraMode from '@/components/ModalDefaultPopupBodyCameraMode.vue';
import CameraPageShootCardAuto from '@/components/CameraPageShootCardAuto.vue';
import CameraPageShootCardFrontBack from '@/components/CameraPageShootCardFrontBack.vue';
import CameraPageShootCardDiagonal from '@/components/CameraPageShootCardDiagonal.vue';
import CameraPageShootFace from '@/components/CameraPageShootFace.vue';
import CameraPageHeader from '@/components/CameraPageHeader.vue';
import CameraPageHeaderPaidy from '@/components/CameraPageHeaderPaidy.vue';
import CameraPageShootButton from '@/components/CameraPageShootButton.vue';
import CameraPageChangeCameraModeButton from '@/components/CameraPageChangeCameraModeButton.vue';
import CameraPageChangeCameraModeMessage from '@/components/CameraPageChangeCameraModeMessage.vue';
import CameraPageShootCheck from '@/components/CameraPageShootCheck.vue';
import CameraPreview, { getCurrentCameraPreview } from '@/components/CameraPreview.vue';
import MnDocType from '@/resources/enum/MnDocType';
import { CameraDevice, FacingMode } from '@/store/modules/camera';

const cameraModule = namespace('camera');

@Component({
  components: {
    ColorButton,
    ModalDefault,
    ModalDefaultPopupBodyCameraMode,
    CameraPageShootCardAuto,
    CameraPageShootCardFrontBack,
    CameraPageShootCardDiagonal,
    CameraPageShootFace,
    CameraPageHeader,
    CameraPageHeaderPaidy,
    CameraPageShootButton,
    CameraPageChangeCameraModeButton,
    CameraPageChangeCameraModeMessage,
    CameraPageShootCheck
  }
})
export default class Page extends Vue {
  private refreshTimerId: number = -1;
  private detectTimerId: number = -1;
  private isShootable: boolean = false;
  private showCameraModeMessage: boolean = true;

  @cameraModule.State('idDocType') idDocType!: IdDocType;

  @Watch('isResize')
  handleResize(value: boolean) {
    if (value) {
      this.isShootable = false;
    } else {
      this.getPosition();
    }
  }

  private get isResize(): boolean {
    return this.$store.state.camera.isResize;
  }

  private get isPaidy(): boolean {
    return this.$store.getters['auth/isPaidy'];
  }

  private get isIdDocFront(): boolean {
    return this.$store.state.camera.shootType === 'identity-document-front';
  }

  private get isIdDocDiagonal(): boolean {
    return this.$store.state.camera.shootType === 'identity-document-diagonal';
  }

  private get isIdDocBack(): boolean {
    return this.$store.state.camera.shootType === 'identity-document-back';
  }

  private get isMnDocFront(): boolean {
    return this.$store.state.camera.shootType === 'mynumber-document-front';
  }

  private get isMnDocBack(): boolean {
    return this.$store.state.camera.shootType === 'mynumber-document-back';
  }

  private get isFaceFront(): boolean {
    return this.$store.state.camera.shootType === 'face-front';
  }

  private get isPC(): boolean {
    return this.$store.getters['camera/isPC'];
  }

  private get canChangeCameraModeManually(): boolean {
    return this.$store.getters['camera/canChangeCameraModeManually'];
  }

  private get showCameraModeModal(): boolean {
    return this.$store.state.camera.showCameraModeModal;
  }

  private get isIdDocPassport(): boolean {
    if (this.$store.state.camera.idDocType === IdDocType.PassportHo) return true;
    if (this.$store.state.camera.idDocType === IdDocType.PassportTo) return true;
    if (this.$store.state.camera.idDocType === IdDocType.PassportTi) return true;
    if (this.$store.state.camera.idDocType === IdDocType.PassportAnyIssuanceDateTo) return true;
    if (this.$store.state.camera.idDocType === IdDocType.PassportAnyIssuanceDateTi) return true;
    return false;
  }

  private get isMnDocResidenceCertificate(): boolean {
    return this.$store.state.camera.mnDocType === MnDocType.ResidenceCertificate;
  }

  private get isGpDocPage(): boolean {
    return this.$store.getters['camera/isGpdoc'];
  }

  private get isOpDocPage(): boolean {
    return this.$store.getters['camera/isOpdoc'];
  }

  private get isGeneralBook(): boolean {
    return this.$store.getters['camera/isGeneralBook'];
  }

  private get isGlobal(): boolean {
    return this.$store.getters['camera/isGlobal'];
  }

  // 手動撮影で撮影済みの状態
  private get isManualShooted(): boolean {
    return !this.$store.state.camera.isDetectStart && this.$store.state.camera.isShooted;
  }

  private get cardWidth(): number {
    return this.$store.state.camera.clippingDisplayWidth * 0.9;
  }

  private get cardHeight(): number {
    return this.cardWidth * this.$store.state.camera.idDocAspectRatioView;
  }

  private get isCameraChanging(): boolean {
    return this.$store.state.camera.isCameraChanging;
  }

  private get cameraModeIndex(): number {
    return (
      this.$store.state.camera.selectedDeviceIndex[this.isFaceFront ? FacingMode.User : FacingMode.Environment] + 1
    );
  }

  public async created(): Promise<void> {
    // 正常撮影開始フラグの確認
    const isStart: boolean = this.$store.state.camera.isStart;
    // 正常撮影開始フラグを戻す
    this.$store.commit('camera/isStart', false);
    // 撮影トリガーを戻す
    this.$store.commit('camera/isShooted', false);
    this.$store.commit('camera/isDetectSuccess', false);
    // ページマウント時の設定
    await this.$store.dispatch('camera/mountedSetting', this.$route.path);
    // 正常撮影開始フラグを確認して説明ページからの正しい遷移以外は説明ページに戻す
    if (!isStart && !this.isGpDocPage && !this.isOpDocPage)
      this.$router.replace(this.$store.getters['camera/locationIntroduction']);
  }

  public async mounted(): Promise<void> {
    // ログの送信
    this.sendLogEnter();

    // 撮影情報を取得して撮影を開始する
    this.getPosition();

    this.setRefreshTimer();
  }

  public beforeDestroy(): void {
    window.clearTimeout(this.refreshTimerId);
    window.clearTimeout(this.detectTimerId);
  }

  // 一定期間以上経過後の処理
  private setRefreshTimer(): void {
    const refreshSecond: number = this.$store.state.camera.isDetectStart ? 30 : 3 * 60;
    this.refreshTimerId = window.setTimeout(this.handleRefresh, refreshSecond * 1000);
  }

  // 撮影ページに入ったログの送信
  private async sendLogEnter(): Promise<void> {
    let logKind: string = '';
    //if (this.isIdDocFront) logKind = 'page_camera_shooting_identity_front';
    //if (this.isIdDocDiagonal) logKind = 'page_camera_shooting_identity_diagonal';
    //if (this.isIdDocBack) logKind = 'page_camera_shooting_identity_back';
    if (this.isMnDocFront) logKind = 'page_camera_shooting_mynumber_front';
    if (this.isMnDocBack) logKind = 'page_camera_shooting_mynumber_back';
    if (this.isGpDocPage) logKind = 'page_camera_shooting_general_page';
    if (this.isOpDocPage) logKind = 'page_camera_shooting_optional_page';
    if (this.isFaceFront) logKind = 'page_camera_shooting_face_front';
    if (logKind === '') return;
    await sendLog({ level: 'INFO', kind: logKind });
  }

  // 撮影情報を取得して撮影を開始する
  private async getPosition(): Promise<void> {
    this.isShootable = false;

    // 撮影処理中だったら撮影開始通信を行わない
    if (this.$store.state.camera.isShooting) return;

    // ランダム枠を取り直す前に自動撮影のカウントをリセットする
    this.$store.commit('camera/resetDetectCount');

    try {
      // 撮影開始通信 ランダム枠の位置を取得する
      await this.$store.dispatch('camera/storeStart');
      // ランダム枠位置の取得に成功したら撮影許可
      this.isShootable = true;
    } catch (error) {
      if (error instanceof ApiCancelError) {
        // キャンセルは握りつぶす
      } else if (error instanceof ApiModalError) {
        // clientRetryableで処理したエラーは握りつぶす
      } else throw error;
    }
  }

  // 一定時間経過後の処理
  private async handleRefresh(): Promise<void> {
    if (this.$store.state.camera.isShooting) {
      // 撮影処理中だったらもう少しだけ待つ
      this.refreshTimerId = window.setTimeout(this.handleRefresh, 100);
    } else if (this.$store.state.camera.isDetectStart && this.$store.state.camera.detectCount > 0) {
      // 自動撮影でうまくいきそうだったらもう少しだけ待つ
      this.refreshTimerId = window.setTimeout(this.handleRefresh, 100);
    } else if (this.$store.state.camera.isDetectStart) {
      // 自動撮影において一定時間経過したら手動撮影への移行を伝えるエラーページへ遷移する
      const fields: string[] = [CameraChildErrorCode.AutodetectTimeout];
      this.$router.push(Object.assign({}, this.$store.getters['camera/locationError'], { query: { fields } }));
    } else if (this.isIdDocFront || this.isIdDocBack) {
      // 本人確認書類の表面撮影か裏面撮影において一定時間経過したらエラーページへ遷移する
      const fields: string[] = [CameraChildErrorCode.ShootTimeout];
      this.$router.push(Object.assign({}, this.$store.getters['camera/locationError'], { query: { fields } }));
    }
  }

  private handleRequestChangeMode(): void {
    this.$store.commit('camera/setReadyCameraModeModal', true);
    this.$store.dispatch('camera/useNextCameraMode', this.isFaceFront ? FacingMode.User : FacingMode.Environment);
  }

  private handleClose(): void {
    this.showCameraModeMessage = false;
  }

  private handleRequestReboot(): void {
    this.$store.commit('camera/setShowCameraModeModal', false);
    this.$store.commit('camera/setIsRefreshRequesting', true);

    // refresh処理のタイマーを一度リセットする
    window.clearTimeout(this.refreshTimerId);
    this.setRefreshTimer();
  }

  // 前撮影用にカメラの画像を取得してbase64に変換する
  private createBase64(isDetect: boolean): {
    detect: string;
    shoot: string;
  } {
    const preview: CameraPreview | null = getCurrentCameraPreview();
    if (preview === null) throw new Error('camera preview is not created');

    const jpegQuarity: number = 0.9;
    const video: HTMLVideoElement = preview.video;
    const shootCanvas: HTMLCanvasElement = document.createElement('canvas');
    const shootContext: CanvasRenderingContext2D | null = shootCanvas.getContext('2d');
    const w: number = this.$store.state.camera.rawBase64Width;
    const h: number = this.$store.state.camera.rawBase64Height;
    const x: number = Math.round((video.videoWidth - w) / 2);
    const y: number = Math.round((video.videoHeight - h) / 2);
    shootCanvas.width = w;
    shootCanvas.height = h;
    if (shootContext) shootContext.drawImage(video, x, y, w, h, 0, 0, w, h);
    const base64Shoot: string = shootCanvas.toDataURL('image/jpeg', jpegQuarity);
    this.checkBase64(base64Shoot);

    if (!isDetect) return { detect: '', shoot: base64Shoot };

    // PC版は自動撮影の画質を変えない
    if (this.isPC) return { detect: base64Shoot, shoot: base64Shoot };

    // 自動撮影の画像容量を減らすために画像のサイズを変える
    const detectCanvas: HTMLCanvasElement = document.createElement('canvas');
    const detectContext: CanvasRenderingContext2D | null = detectCanvas.getContext('2d');
    detectCanvas.width = 320;
    detectCanvas.height = Math.round(detectCanvas.width * (h / w));
    const srcw: number = shootCanvas.width;
    const srch: number = shootCanvas.height;
    const dstw: number = detectCanvas.width;
    const dsth: number = detectCanvas.height;
    if (detectContext) detectContext.drawImage(shootCanvas, 0, 0, srcw, srch, 0, 0, dstw, dsth);
    const base64Detect: string = detectCanvas.toDataURL('image/jpeg', jpegQuarity);
    this.checkBase64(base64Detect);

    return { detect: base64Detect, shoot: base64Shoot };
  }

  // base64の確認
  private checkBase64(base64: string): void {
    if (base64 !== '' && base64 !== 'data:,') return;
    // もし空画像になってしまったらエラーページへ遷移し、エラーを投げることによりログを飛ばす
    const code: ErrorCode = ErrorCode.Unknown;
    this.$router.push({ name: 'application-error', params: { code } });
    const w: number = this.$store.state.camera.rawBase64Width;
    const h: number = this.$store.state.camera.rawBase64Height;
    throw new Error(`empty image from canvas.toDataURL (width: ${w}, height: ${h})`);
  }

  // 自動撮影
  private async onDetect(): Promise<void> {
    if (this.$store.state.camera.isShooting) return;

    try {
      this.$store.commit('camera/isShooting', true);

      // 画像作成
      const base64: {
        detect: string;
        shoot: string;
      } = this.createBase64(true);

      // 画像確認 通信エラーは無視する
      try {
        await this.$store.dispatch('camera/storeDetectData', base64.detect);
      } catch {}

      if (this.$store.state.camera.detectCount >= 3) {
        // 画像送信
        await this.$store.dispatch('camera/storeShootData', base64.shoot);
        await this.$store.dispatch('camera/storeTrimmedPhotoData');
        this.$store.commit('camera/isDetectSuccess', true);

        // 演出待ち
        await new Promise((resolve) => (this.detectTimerId = window.setTimeout(resolve, 1000)));

        // ページ遷移
        this.$router.push(this.$store.getters['camera/locationConfirmation']);
      }
    } catch (error) {
      const data: ErrorType = (axios.isAxiosError(error) && error.response && error.response.data) || {};
      if (error instanceof ApiCancelError) {
        // キャンセルは握りつぶす
      } else if (error instanceof ApiModalError) {
        // clientRetryableで処理したエラーは握りつぶす
      } else if (
        data.error_code === ErrorCode.CVInvalidFeature ||
        data.error_code === ErrorCode.CVDifferentDriverLicense
      ) {
        // 撮影結果の自動判定エラー処理
        this.$store.commit('camera/addRetryCount');
        const isManyRetry: boolean = this.$store.state.camera.retryCount >= 5;
        const errorCodes: string[] = isManyRetry ? [CameraChildErrorCode.ManyRetry] : data.fields || [];
        const fields: string[] = [CameraChildErrorCode.Automatic as string].concat(errorCodes);
        this.$router.push(Object.assign({}, this.$store.getters['camera/locationError'], { query: { fields } }));
      } else throw error;
    } finally {
      this.$store.commit('camera/isShooting', false);
    }
  }

  // 手動撮影
  private async onShoot(): Promise<void> {
    if (this.$store.state.camera.isShooting) return;

    try {
      this.$store.commit('sensorMotion/addTriggerCountShoot');
      this.$store.commit('camera/isShooting', true);
      this.$store.commit('camera/isShooted', true);

      // 画像作成
      const base64: {
        detect: string;
        shoot: string;
      } = this.createBase64(false);

      // 画像送信
      await this.$store.dispatch('camera/storeShootData', base64.shoot);
      await this.$store.dispatch('camera/storeTrimmedPhotoData');

      // ページ遷移
      this.$router.push(this.$store.getters['camera/locationConfirmation']);
    } catch (error) {
      const data: ErrorType = (axios.isAxiosError(error) && error.response && error.response.data) || {};
      if (error instanceof ApiCancelError) {
        // キャンセルは握りつぶす
      } else if (error instanceof ApiModalError) {
        // clientRetryableで処理したエラーは握りつぶす
      } else if (
        data.error_code === ErrorCode.CVInvalidFeature ||
        data.error_code === ErrorCode.CVDifferentDriverLicense
      ) {
        // 撮影結果の自動判定エラー処理
        this.$store.commit('camera/addRetryCount');
        const isManyRetry: boolean = this.$store.state.camera.retryCount >= 5;
        const errorCodes: string[] = isManyRetry ? [CameraChildErrorCode.ManyRetry] : data.fields || [];
        const fields: string[] = [CameraChildErrorCode.Automatic as string].concat(errorCodes);
        this.$router.push(Object.assign({}, this.$store.getters['camera/locationError'], { query: { fields } }));
      } else throw error;
    } finally {
      this.$store.commit('camera/isShooting', false);
    }
  }

  private onSkip(): void {
    if (this.$store.state.camera.isShooting) return;
    // モバイルでタップ時にアウトラインを表示するために遅延させる
    setTimeout(() => {
      this.$router.push(this.$store.getters['camera/locationSkip']);
    }, 50);
  }
}
